import * as React from 'react'

/**
 * An open tab learns that a newer Patchbay is live (#994). The web bundle is stamped with the deployed git SHA
 * (`PATCHBAY_BUILD`, vite `define`); `/api/health` `version` is the same SHA. On focus and every 5 minutes the tab
 * compares them. A mismatch shows a small bar; the tab reloads by itself only when nothing could be lost.
 */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000

export const BUILD_ID: string = typeof __PATCHBAY_BUILD__ === 'string' ? __PATCHBAY_BUILD__ : 'dev'

/** True only when both ids are real and different. Dev builds and an unreadable answer never prompt a reload. */
export function isStaleBuild(current: string, live: string | null | undefined): boolean {
  if (!live || !current || current === 'dev' || current === 'unknown' || live === 'unknown') return false
  return current !== live
}

const TEXT_INPUT_TYPES = new Set(['text', 'email', 'tel', 'url', 'number', 'password', 'date', 'time', 'datetime-local'])

/**
 * Could a reload lose something? Deliberately conservative: an open dialog, or any non-empty text field, textarea or
 * contenteditable (search boxes excepted) counts as dirty. Where we cannot be sure we say "dirty": the bar stays.
 */
export function hasUnsavedWork(doc: Document = document): boolean {
  if (doc.querySelector('dialog[open], [role="dialog"], [role="alertdialog"], [aria-modal="true"]')) return true
  for (const el of doc.querySelectorAll('textarea')) if ((el as HTMLTextAreaElement).value.trim() !== '') return true
  for (const el of doc.querySelectorAll('[contenteditable=""], [contenteditable="true"]')) {
    if ((el.textContent ?? '').trim() !== '') return true
  }
  for (const el of doc.querySelectorAll('input')) {
    const input = el as HTMLInputElement
    if (TEXT_INPUT_TYPES.has(input.type) && input.value.trim() !== '') return true
  }
  return false
}

export async function fetchLiveVersion(fetchFn: typeof fetch = (...a) => globalThis.fetch(...a)): Promise<string | null> {
  try {
    const res = await fetchFn('/api/health', { cache: 'no-store', credentials: 'same-origin' })
    if (!res.ok) return null
    const body = (await res.json()) as { version?: unknown }
    return typeof body.version === 'string' ? body.version : null
  } catch {
    return null
  }
}

export interface VersionCheckDeps {
  current?: string
  fetchLive?: () => Promise<string | null>
  dirty?: () => boolean
  reload?: () => void
}

/** One comparison. Returns whether the tab is stale and whether it reloaded itself. */
export async function runVersionCheck(deps: VersionCheckDeps = {}): Promise<{ stale: boolean; reloaded: boolean }> {
  const live = await (deps.fetchLive ?? fetchLiveVersion)()
  if (!isStaleBuild(deps.current ?? BUILD_ID, live)) return { stale: false, reloaded: false }
  if (!(deps.dirty ?? hasUnsavedWork)()) {
    ;(deps.reload ?? (() => globalThis.location.reload()))()
    return { stale: true, reloaded: true }
  }
  return { stale: true, reloaded: false }
}

/** Runs the check on focus and every 5 minutes. Returns whether to show the update bar. */
export function useStaleBuild(deps: VersionCheckDeps = {}, intervalMs = VERSION_CHECK_INTERVAL_MS): boolean {
  const [stale, setStale] = React.useState(false)
  const depsRef = React.useRef(deps)
  depsRef.current = deps
  React.useEffect(() => {
    let alive = true
    const check = () => {
      void runVersionCheck(depsRef.current).then((r) => {
        if (alive && r.stale) setStale(true)
      })
    }
    const timer = setInterval(check, intervalMs)
    window.addEventListener('focus', check)
    return () => {
      alive = false
      clearInterval(timer)
      window.removeEventListener('focus', check)
    }
  }, [intervalMs])
  return stale
}

export function UpdateBar({ deps, intervalMs }: { deps?: VersionCheckDeps; intervalMs?: number }) {
  const stale = useStaleBuild(deps, intervalMs)
  if (!stale) return null
  return (
    <div
      role="status"
      data-banner="update-available"
      className="fixed inset-x-0 top-3 z-[100] mx-auto flex w-fit max-w-[92vw] items-center gap-3 rounded-md border border-primary bg-card px-4 py-2 text-sm font-medium text-foreground shadow-xl"
    >
      <span>Patchbay was updated.</span>
      <button
        type="button"
        className="rounded bg-primary px-3 py-1 font-medium text-primary-foreground hover:bg-primary/90"
        onClick={() => globalThis.location.reload()}
      >
        Reload
      </button>
    </div>
  )
}
